// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { RefObject } from 'react';

import type { Resource, ResourceKey, SurveyPart, SurveyPartPatch, SurveyType } from '../../../api/types';
import { fieldKeys, useTextDraft } from '../../../app/useTextDraft';
import { PANEL_FIELD_KEYS } from '../../../kortlaegning/resources';
import { TextInput } from '../../TextInput';
import { ResourceCell } from '../../kortlaegning/ResourceCell';
import { FieldRow } from './FieldRow';
import styles from './Inspector.module.css';
import { NumberWithUnit } from './NumberWithUnit';

export interface PartFieldsProps {
  type: SurveyType;
  part: SurveyPart;
  resource: Resource | null;
  catalogue: readonly ResourceKey[];
  canEdit: boolean;
  home: RefObject<HTMLElement | null>;
  onPatch: (patch: SurveyPartPatch) => void;
  onCellCommit: (keyId: string, value: string | null, label: string) => Promise<void>;
  onInvalid: (label: string) => void;
  /**
   * `inline`: inside the type's inspector (Gennemgang's `PartFocus`) — the
   * quantity reads "denne del" and the part's values fold away under
   * "Delens egenskaber". `page`: the part's own inspector (`PartInspector`) —
   * the values open under "Egenskaber".
   */
  layout: 'inline' | 'page';
}

function NoteField({ part, canEdit, onPatch, home }: Pick<PartFieldsProps, 'part' | 'canEdit' | 'onPatch' | 'home'>) {
  const draft = useTextDraft(part.note, (note) => onPatch({ note }));
  const keys = fieldKeys(draft, home);
  return (
    <TextInput
      variant="filled"
      size="lg"
      block
      aria-label={`Note for ${part.code}`}
      readOnly={!canEdit}
      {...draft.props}
      onKeyDown={keys}
    />
  );
}

/**
 * A part's own rows (the old DetailPanel's part-level edits): its quantity,
 * its note and every part-scoped value from the catalogue (the room and the
 * keys the panel shows elsewhere left out). Shared by `PartFocus` and
 * `PartInspector`; every write goes through the callbacks.
 */
export function PartFields(props: PartFieldsProps) {
  const { type, part, resource, catalogue, canEdit, home, onPatch, onCellCommit, onInvalid, layout } = props;
  const keys = catalogue.filter((k) => k.scope === 'part' && !PANEL_FIELD_KEYS.has(k.id) && k.id !== 'sys:room');
  const rows = keys.map((k) => (
    <FieldRow
      key={k.id}
      label={k.label}
      control={
        <ResourceCell
          resourceKey={k}
          value={resource?.values[k.id] ?? null}
          editing={canEdit && k.editable}
          variant="field"
          onCommit={(v) => onCellCommit(k.id, v, k.label)}
          onInvalid={onInvalid}
          home={home}
        />
      }
    />
  ));
  return (
    <>
      <FieldRow
        label={layout === 'inline' ? 'Mængde (denne del)' : 'Mængde'}
        control={
          <NumberWithUnit
            key={part.code}
            label={`Mængde for ${part.code}`}
            value={part.quantity}
            unit={type.unit}
            readOnly={!canEdit}
            home={home}
            onInvalid={onInvalid}
            onCommit={(q) => q !== null && onPatch({ quantity: q })}
          />
        }
      />
      <FieldRow label="Note" control={<NoteField key={part.code} part={part} canEdit={canEdit} onPatch={onPatch} home={home} />} />
      {keys.length > 0 &&
        (layout === 'inline' ? (
          <details className={styles.partProps}>
            <summary>Delens egenskaber</summary>
            {rows}
          </details>
        ) : (
          <section className={styles.section} aria-label="Delens egenskaber">
            <h3 className={styles.sectionHead}>Egenskaber</h3>
            {rows}
          </section>
        ))}
    </>
  );
}
